import styled from 'styled-components';
import { useGetUserSearchAutocomplete } from '../../../api/user/use-get-user-search';
import { AutoComplete as AntAutoComplete, Avatar, Form } from 'antd';
import React, { useMemo } from 'react';
import { getHighlightedText } from 'pages/project-visualisation/components/search/canvas/options/utils';
import { UserData } from 'api/types';
import { getAvatarPath } from '../../../helpers/utils';

type SelectProp = { id: string; email: string; name: string };

export const UserItem = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 3rem;

  .user-name {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const renderUsers = (user: UserData, search: string) => ({
  id: user.id,
  value: user.id,
  name: `${user.first_name} ${user.last_name}`,
  email: user.email,
  label: (
    <UserItem>
      <div className="user-name">
        <Avatar src={getAvatarPath(user.avatar)} />
        {getHighlightedText(`${user.first_name} ${user.last_name}`, search)}
      </div>
    </UserItem>
  ),
});

export const AutoCompleteUser: React.FC<{
  search: string | undefined;
  setSearch: React.Dispatch<React.SetStateAction<string | undefined>>;
  children: React.ReactNode;
}> = ({ search, setSearch, children }) => {
  const form = Form.useFormInstance();

  const { data } = useGetUserSearchAutocomplete(search?.trim() ?? '', {
    enabled: search ? search.trim()?.length > 2 : false,
  });

  const options = useMemo(
    () =>
      data?.rows.map((user) => ({
        key: user.id,
        value: user.id,
        id: user.id,
        email: user.email,
        name: `${user.first_name} ${user.last_name}`,
        label: renderUsers(user, search ?? '').label,
      })),
    [data?.rows, search]
  );

  const onSelect = (value: string, option: SelectProp) => {
    setSearch(option.name);
    form.setFieldsValue({ email: option.email, userId: option.id, userEmail: option.email });
  };

  return (
    <AntAutoComplete
      popupClassName="search-perspective-users"
      dropdownStyle={{
        backdropFilter: 'blur(7px)',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%)',
      }}
      onSelect={onSelect}
      options={options}
      value={search}
    >
      {children}
    </AntAutoComplete>
  );
};
